import React, { useCallback, useMemo, useState } from 'react';
import { Form } from 'react-bootstrap';
import { ChatPanel } from '../../../Components/ChatWidget';
import CounterpartyPicker, { KIND_LABELS } from '../../../Components/CounterpartyPicker';
import { SCOPES } from '../../../Services/KnowledgeBaseService';

/**
 * "What would an operator actually be told?"
 *
 * The real chat — the same hook, thread, block renderer and composer as the
 * floating widget, in a panel instead of a card (see ChatPanel). This answers
 * the question the retrieval preview cannot: whether the chunks that came
 * back actually turn into a usable answer.
 *
 * SCOPING: the agent's wire protocol is fixed (`{prompt}` to a runtime ARN
 * with a session-id header) and adding a knowledge-base field to it would be
 * an API change out of this screen's scope. So the scope goes in the WORDS of
 * the prompt, via ChatPanel's `transformMessage`, and the operator still sees
 * their own question rather than the preamble wrapped around it.
 *
 * ASKING IS NOT PERMISSION-GATED. An operator asking "how do I report a
 * no-show?" retrieves ungated; only managing the corpus is gated. This tab is
 * therefore rendered for everybody who can reach the screen.
 */

const EXAMPLES = [
  'How can I report a no-show?',
  'What is the waiting time policy?',
  'Who do I call about a damaged vehicle?',
];

function KnowledgeBaseAsk({ knowledgeBase }) {
  const [scope, setScope] = useState('');
  const [counterparty, setCounterparty] = useState({ id: '', name: '' });

  const asCounterparty = scope && counterparty.id
    ? { kind: scope, name: counterparty.name || counterparty.id }
    : null;

  const transformMessage = useCallback((text) => {
    const parts = [
      `[Operational question. Search the uploaded "${knowledgeBase.name}" documents`,
      ' before answering and name the source file for anything you quote.',
    ];
    if (asCounterparty) {
      parts.push(
        ` Treat this as asked by the ${asCounterparty.kind} "${asCounterparty.name}":`,
        ' use their own documents where they have them, and say when an answer',
        ' comes from the general policy instead.',
      );
    }
    parts.push(']\n\n', text);
    return parts.join('');
  }, [knowledgeBase.name, asCounterparty]);

  // Remounting on a scope change starts a clean conversation: the preamble
  // only reaches the agent on the turn it is sent, so a mid-thread switch
  // would otherwise leave the earlier turns scoped to the previous
  // counterparty and the agent reconciling two.
  const panelKey = `${knowledgeBase.id}|${scope}|${counterparty.id}`;

  const subtitle = useMemo(() => (asCounterparty
    ? `as ${asCounterparty.kind} ${asCounterparty.name}`
    : knowledgeBase.name), [asCounterparty, knowledgeBase.name]);

  return (
    <div>
      <p className="text-muted small">
        The chat an operator actually gets, pointed at this knowledge base.
        Asking is open to everyone — only managing the documents is restricted.
      </p>

      <div className="row g-2 align-items-end mb-3">
        <div className="col-12 col-md-4">
          <Form.Group controlId="kb-ask-scope">
            <Form.Label className="small text-muted mb-1">Ask as</Form.Label>
            <Form.Select
              value={scope}
              onChange={(e) => {
                setScope(e.target.value);
                setCounterparty({ id: '', name: '' });
              }}
            >
              <option value="">Nobody in particular</option>
              <option value={SCOPES.OPERATOR}>An operator</option>
              <option value={SCOPES.PARTNER}>A partner</option>
            </Form.Select>
          </Form.Group>
        </div>
        {scope && (
          <div className="col-12 col-md-8">
            <CounterpartyPicker
              kind={scope}
              value={counterparty.id}
              onChange={(id, name) => setCounterparty({ id, name })}
              id={`kb-ask-${scope}`}
              label={null}
              placeholder={`Search ${KIND_LABELS[scope].toLowerCase()}s — optional`}
            />
          </div>
        )}
      </div>

      <ChatPanel
        key={panelKey}
        storageNamespace="kb-ask"
        title="Ask the assistant"
        icon="fa-magic"
        subtitle={subtitle}
        placeholder={`Ask an operational question — answers come from the documents in “${knowledgeBase.name}”.`}
        composerPlaceholder="How can I report a no-show?"
        examples={EXAMPLES}
        transformMessage={transformMessage}
      />
    </div>
  );
}

export default KnowledgeBaseAsk;
